import { useEffect, useState } from "react"
import { FeedbackQuestionType, FeedbackType } from "../../common/constants"
import TextInput from "../TextInput"
import styles from "./FeedbackEditor.module.css"
import Select from "../Select"
import SimpleCollapsable from "../SimpleCollapsable"
import { v4 } from "uuid";
import { Each } from "../../common/Each"
import typo from "../../typography.module.css"
import Button from "../Button"
import { ReactComponent as AddIcon } from "../../assets/images/icons/ic-add-circle.svg"
import { ReactComponent as CloseIcon } from "../../assets/images/icons/ic-close.svg"
import Checkbox from "../Checkbox"
import MultipleSelect from "../MultipleSelect"
import api from "../../api"
import {
	BlockTypeSelect,
	BoldItalicUnderlineToggles,
	CreateLink,
	diffSourcePlugin,
	DiffSourceToggleWrapper,
	headingsPlugin,
	linkDialogPlugin,
	listsPlugin,
	ListsToggle,
	MDXEditor,
	quotePlugin,
	Separator,
	tablePlugin,
	thematicBreakPlugin,
	toolbarPlugin,
	UndoRedo
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { ReactSortable } from "react-sortablejs"


const FeedbackEditor = ({ feedback = null, courses = [], onValidityChange = () => { }, onChange = () => { } }) => {
	const statusTypes =
		[
			{ id: 0, name: 'Bozza', value: 'draft' },
			{ id: 1, name: 'Pubblico', value: 'public' },
			{ id: 2, name: 'Nascosto', value: 'hidden' }
		]

	const resourceTypes = [
		{ id: 0, name: "Corso", value: FeedbackType.Course },
		{ id: 1, name: "Lezione", value: FeedbackType.Lesson },
	]

	const questionTypes = [
		{ id: 0, name: "Quantitativa", value: FeedbackQuestionType.Quantitative },
		{ id: 1, name: "Qualitativa", value: FeedbackQuestionType.Qualitative },
		{ id: 2, name: "Aperta", value: FeedbackQuestionType.Open },

	]

	const courseOptions = courses.map((c, i) => ({ id: i, name: c.name, value: c.id }))

	const getDefaultQuestion = () => ({
		id: v4(),
		question: "",
		type: FeedbackQuestionType.Quantitative,
	})

	const [name, setName] = useState("")
	const [description, setDescription] = useState("")
	const [content, setContent] = useState([getDefaultQuestion()])
	const [status, setStatus] = useState(statusTypes[0])
	const [resourceType, setResourceType] = useState(resourceTypes[0])
	const [resourceId, setResourceId] = useState(courseOptions[0])
	const [excluded, setExcluded] = useState([])
	const [editions, setEditions] = useState([])
	const [mandatory, setMandatory] = useState(false)
	const [randomId, setRandomId] = useState(v4())

	useEffect(() => {
		if (!feedback) return
		const _resourceType = resourceTypes.find(r => r.value === feedback.resource_type)
		setName(feedback.name)
		setDescription(feedback.description)
		setContent(feedback.content)
		setStatus(statusTypes.find(s => s.value === feedback.status))
		setResourceType(_resourceType)
		setResourceId(_resourceType.value === FeedbackType.Course ? courseOptions.find(c => c.value === feedback.resource_id) : null)
		setMandatory(feedback.mandatory)
		setExcluded(feedback.excluded?.editions || [])
		setRandomId(v4())

	}, [feedback])

	useEffect(() => {
		onValidityChange(!!name && isContentValid() && isResourceValid())
	}, [name, content, resourceType, resourceId])

	useEffect(() => {
		onChange({
			name,
			description,
			content,
			status: status.value,
			resource_type: resourceType.value,
			resource_id: resourceId?.value ?? resourceId,
			excluded: resourceType?.value === FeedbackType.Course ? { editions: excluded } : null,
			mandatory
		})
	}, [name, description, content, status, resourceType, resourceId, mandatory, excluded])

	const isContentValid = () => content.every(item => item.question)
	const isResourceValid = () => {
		if (resourceType.value === FeedbackType.Course) {
			return resourceId?.value !== null
		}
		return true
	}

	const getCourseEditions = async (courseId) => {
		try {
			let editions = await api.get(`/admin/courses/${courseId}/editions`)
			setEditions(editions)
		}
		catch (error) {
			console.error(error)
		}
	}

	useEffect(() => {
		if (resourceType && resourceId && resourceType?.value === FeedbackType.Course && resourceId?.value !== null) {
			getCourseEditions(resourceId.value)
		}
	}, [resourceId, resourceType])

	return (
		<div className={styles.container}>
			<Select appearance="modern" portal
				options={statusTypes}
				selected={status}
				onSelect={v => setStatus(v)}
			/>

			<TextInput appearance="modern"
				type="text"
				value={name}
				placeholder="Nome"
				onKeyUp={v => setName(v)}
			/>


			<div className={styles.editor}>
				<MDXEditor
					key={randomId}
					className={styles.markdown}
					markdown={description}
					onChange={(md) => {
						setDescription(md)
					}}
					plugins={[
						headingsPlugin(),
						listsPlugin(),
						quotePlugin(),
						thematicBreakPlugin(),
						linkDialogPlugin(),
						diffSourcePlugin(),
						tablePlugin(),
						toolbarPlugin({
							toolbarContents: () => (
								<>
									{' '}
									<UndoRedo />
									<Separator />
									<BoldItalicUnderlineToggles />
									<Separator />
									<ListsToggle />
									<CreateLink />
									{/* <InsertTable /> */}
									<Separator />
									<BlockTypeSelect />
									<DiffSourceToggleWrapper options={["rich-text", "source"]} />
								</>
							)
						})
					]}
				/>
			</div>

			{/* 
			<TextareaAutosize
				value={description}
				minRows={1} maxRows={3}
				type="text"
				placeholder={"Descrizione"}
				className={styles.textArea}
				onChange={e => setDescription(e.target.value)} /> */}

			<Select appearance="modern" portal
				options={resourceTypes}
				selected={resourceType}
				onSelect={v => setResourceType(v)}
			/>

			<SimpleCollapsable expanded={resourceType.value === FeedbackType.Course}>
				<div>
					<Select appearance="modern" portal
						options={courseOptions}
						selected={resourceId}
						onSelect={s => setResourceId(s)}
					/>
				</div>
			</SimpleCollapsable>
			{resourceType.value === FeedbackType.Course &&
				<MultipleSelect
					selected={editions?.filter(e => !excluded.includes(e.id))}
					options={editions}
					allLabel="Tutte le edizioni"
					placeholder={"Seleziona Edizioni"}
					onSelect={(items) => {
						setExcluded(editions.filter(e => !items.map(i => i.id).includes(e.id)).map(e => e.id))
					}} />
			}

			<div className={styles.checkbox}>
				<Checkbox label="Obbligatorio" value={mandatory} onChange={m => setMandatory(m)} />
			</div>
			<ReactSortable list={content} setList={setContent} className={styles.questions}>
			<Each
				of={content}
				render={(item, index) => (
					<div className={styles.question}>
						<div className={typo.caption}>Domanda {index + 1}</div>
						{
							index > 0 &&
							<button className={styles.closeButton} onClick={() => setContent(content => {
								const idx = content.findIndex(c => c.id === item.id)
								content.splice(idx, 1)
								return [...content]
							})}>
								<CloseIcon className={styles.closeIcon}></CloseIcon>
							</button>
						}
						<TextInput appearance="modern"
							type="text"
							value={item.question}
							placeholder="Domanda"
							onKeyUp={v => {
								setContent(content => {
									const el = content.find(c => c.id === item.id)
									el.question = v
									return [...content]
								})
							}}
						/>
						<TextInput appearance="modern"
							type="text"
							value={item.subtitle}
							placeholder="Sottotitolo"
							onKeyUp={v => {
								setContent(content => {
									const el = content.find(c => c.id === item.id)
									el.subtitle = v
									return [...content]
								})
							}}
						/>

						<Select appearance="modern" portal
							options={questionTypes}
							selected={questionTypes.find(q => q.value === item.type)}
							onSelect={s => setContent(content => {
								const el = content.find(c => c.id === item.id)
								el.type = s.value
								return [...content]
							})}
						/>
					</div>
				)}
			/>
			</ReactSortable>
			<Button
				appearance="default"
				additionalClass={styles.addQuestionButton}
				onClick={() => setContent(content => {
					content.push(getDefaultQuestion())
					return [...content]
				})}>
				AGGIUNGI DOMANDA
				<AddIcon />
			</Button>
		</div>
	)
}

export default FeedbackEditor